export function createRequestCoordinator() {
  return {
    version: 0,
    active: null
  };
}

export function startRequest(coordinator, options = {}) {
  const active = coordinator?.active || null;
  if (active?.controller) {
    active.controller.abort();
  }
  const version = coordinator.version + 1;
  const controller = options.abort === false ? null : new AbortController();
  coordinator.version = version;
  coordinator.active = { version, controller };
  return {
    version,
    signal: controller ? controller.signal : null,
    abort() {
      if (controller) {
        controller.abort();
      }
    },
    isCurrent() {
      return coordinator.version === version;
    },
    commit(commitFn) {
      if (!this.isCurrent()) {
        return false;
      }
      commitFn();
      return true;
    }
  };
}

export function cancelRequest(coordinator) {
  const active = coordinator?.active || null;
  if (active?.controller) {
    active.controller.abort();
  }
  if (coordinator) {
    coordinator.version += 1;
    coordinator.active = null;
  }
}

export function captureRequest(coordinator) {
  if (!coordinator) {
    return null;
  }
  const version = coordinator.version;
  const signal = coordinator.active?.controller ? coordinator.active.controller.signal : null;
  return {
    signal,
    isCurrent() {
      return coordinator.version === version;
    }
  };
}

export function requestsCurrent(...requests) {
  return requests.every((request) => !request || typeof request.isCurrent !== "function" || request.isCurrent());
}

export function composeAbortSignal(...signals) {
  const activeSignals = signals.filter(Boolean);
  if (activeSignals.length === 0) {
    return null;
  }
  if (activeSignals.length === 1) {
    return activeSignals[0];
  }
  if (typeof AbortSignal !== "undefined" && typeof AbortSignal.any === "function") {
    return AbortSignal.any(activeSignals);
  }
  const controller = new AbortController();
  const cleanupEntries = [];
  const cleanup = () => {
    for (const entry of cleanupEntries) {
      entry.signal.removeEventListener("abort", entry.listener);
    }
    cleanupEntries.length = 0;
  };
  const abort = () => {
    if (!controller.signal.aborted) {
      controller.abort();
    }
    cleanup();
  };
  for (const signal of activeSignals) {
    if (signal.aborted) {
      abort();
      break;
    }
    const listener = () => abort();
    cleanupEntries.push({ signal, listener });
    signal.addEventListener("abort", listener, { once: true });
  }
  return controller.signal;
}

export function isAbortError(error) {
  return Boolean(error) && (error.name === "AbortError" || error.code === 20);
}
